import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../../supabaseClient';
import { ChevronUpDownIcon, DocumentTextIcon, DocumentArrowDownIcon } from '@heroicons/react/24/solid'; // Importing Heroicons
import { postRequest } from './../../../helpers/apiHelper'; // Adjusted import path for postRequest

const PortalProperties = () => {
  const navigate = useNavigate();

  // State variables to manage data, loading, and error states
  const [propertyScans, setPropertyScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // State variables to manage loading states for actions
  const [loadingViewReportUUID, setLoadingViewReportUUID] = useState(null);
  const [loadingDownloadReportUUID, setLoadingDownloadReportUUID] = useState(null);
  const [analyseError, setAnalyseError] = useState(null); // Moved inside to manage errors globally

  // Helper function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return '';

    // Replace space with 'T' to conform to ISO 8601
    let isoString = dateString.replace(' ', 'T');

    // Check if timezone offset is in the format +HH or -HH
    if (/([+-]\d{2})$/.test(isoString)) {
      // Append ':00' to make it +HH:00 or -HH:00
      isoString += ':00';
    }

    const date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return 'Invalid Date';
    }

    // Format the date to a more readable format
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  // useEffect hook to fetch data when the component mounts
  useEffect(() => {
    const fetchPropertyScans = async () => {
      setLoading(true); // Start loading
      setError(null);   // Reset previous errors

      try {
        // Get the current user
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          console.error('Error fetching user:', userError.message);
          setError(userError.message);
          setLoading(false);
          return;
        }

        setUser(user);

        // Perform a SELECT * query on the property_scans table where user_id == current user id
        const { data, error } = await supabase
          .from('property_scans')
          .select('*')
          .eq('user_id', user.id)
          .order('last_uploaded_at', { ascending: false }); // Order by last_uploaded_at descending

        if (error) {
          console.error('Error fetching property scans:', error.message);
          setError(error.message);
        } else {
          setPropertyScans(data);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred.');
      } finally {
        setLoading(false); // End loading
      }
    };

    fetchPropertyScans();
  }, []);

  // Function to handle 'Review' button click
  const handleReview = (property_uuid) => {
    // Navigate to the Review page with property_uuid
    navigate(`/portal/property?property_uuid=${property_uuid}`);
  };

  // Function to handle 'View report' button click
  const handlePreview = async (property_uuid) => {
    try {
      // Set loading state for this property
      setLoadingViewReportUUID(property_uuid);
      setAnalyseError(null);

      // Make POST request to '/createPropertyValuation' with property_uuid in the body
      await postRequest('/createPropertyValuation', { propertyUUID: property_uuid });

      // After receiving response, navigate to the Report page
      navigate(`/portal/viewReport?property_uuid=${property_uuid}`);
    } catch (error) {
      // Handle error
      console.error('Error creating property valuation:', error);
      setAnalyseError(error.message);
    } finally {
      // Reset loading state
      setLoadingViewReportUUID(null);
    }
  };

  // Function to handle 'Download report' button click
  const downloadReport = async (property_uuid) => {
    try {
      // Set loading state for this property
      setLoadingDownloadReportUUID(property_uuid);
      setAnalyseError(null);

      // Make POST request to '/createPropertyValuation' with property_uuid in the body
      await postRequest('/createPropertyValuation', { propertyUUID: property_uuid });

      // After receiving response, download the report
      const base_url = window.location.origin;
      const jobSheetURL = `${base_url}/report?property_uuid=${property_uuid}`;
      console.log(jobSheetURL);
      const restpackURLAccessToken = "UF4bC9IqnfHQCqYGtLQpZGPG8bB921Fr6SDT08GM5gZqV5hR"; // Replace with your actual access token
      const restpackURL = `https://restpack.io/api/html2pdf/v7/convert?access_token=${restpackURLAccessToken}&url=${encodeURIComponent(jobSheetURL)}&pdf_page=A4&delay=3000`;

      // Directly download the file
      const response = await fetch(restpackURL);
      if (!response.ok) {
        throw new Error('Failed to fetch the PDF report.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // The filename you want
      a.download = `Report_${property_uuid}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Handle error
      console.error('Error downloading report:', error);
      alert('An error has occurred while trying to download the report PDF');
      setAnalyseError(error.message);
    } finally {
      // Reset loading state
      setLoadingDownloadReportUUID(null);
    }
  };

  return (
    <div className="p-8">
      <h2 className="text-3xl font-bold mb-4">Properties</h2>
      <p>See all properties scanned below</p>

      {/* Loading Indicator */}
      {loading && (
        <div className="mt-4 flex justify-center">
          <svg
            className="animate-spin h-5 w-5 text-gray-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            ></path>
          </svg>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="mt-4 text-red-500">Error: {error}</p>}

      {/* Data Table */}
      {!loading && !error && propertyScans.length > 0 && (
        <div className="mt-8 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 rounded-lg border border-gray-200">
            <thead className="bg-gray-50 rounded-t-lg">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                >
                  Property Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                >
                  Property Address
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                >
                  Last Uploaded
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {propertyScans.map((property) => (
                <tr key={property.id} className="hover:bg-gray-100">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                    {property.property_name}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                    {property.property_address}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(property.last_uploaded_at)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm flex space-x-2">
                    {/* Edit Button */}
                    <button
                      onClick={() => handleReview(property.property_uuid)}
                      className="bg-yellow-500 text-black px-3 py-1 w-24 rounded-md flex items-center justify-center hover:bg-yellow-600 transition duration-200"
                    >
                      <ChevronUpDownIcon className="h-5 w-5 mr-1" />
                      Edit
                    </button>

                    {/* View Report Button */}
                    {loadingViewReportUUID === property.property_uuid ? (
                      // Show loading animation for View Report
                      <div className="bg-yellow-500 text-black px-3 py-1 w-28 rounded-md flex items-center justify-center">
                        <svg
                          className="animate-spin h-5 w-5 text-black"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                      </div>
                    ) : (
                      <button
                        onClick={() => handlePreview(property.property_uuid)}
                        className="bg-yellow-500 text-black px-3 py-1 w-28 rounded-md flex items-center justify-center hover:bg-yellow-600 transition duration-200"
                      >
                        <DocumentTextIcon className="h-5 w-5 mr-1" />
                        View Report
                      </button>
                    )}

                    {/* Download Report Button */}
                    {loadingDownloadReportUUID === property.property_uuid ? (
                      // Show loading animation for Download Report
                      <div className="bg-yellow-500 text-black px-3 py-1 w-32 rounded-md flex items-center justify-center">
                        <svg
                          className="animate-spin h-5 w-5 text-black"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                      </div>
                    ) : (
                      <button
                        onClick={() => downloadReport(property.property_uuid)}
                        className="bg-yellow-500 text-black px-3 py-1 w-32 rounded-md flex items-center justify-center hover:bg-yellow-600 transition duration-200"
                      >
                        <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
                        Download Report
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Analyse Action Error Message */}
          {analyseError && <p className="mt-2 text-red-500">{analyseError}</p>}
        </div>
      )}

      {/* No Data Message */}
      {!loading && !error && propertyScans.length === 0 && (
        <p className="mt-4 text-gray-700">No property scans found.</p>
      )}
    </div>
  );
};

export default PortalProperties;
